import React, { memo, useCallback, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import Slick from 'react-slick';
import classnames from 'classnames';
import { createUseStyles, Icon } from '@Sage/hoi-poi-ui';

import FullModalBlank from 'components/FullModalBlank';

import styles from './styles';
const useStyles = createUseStyles(styles, 'CarouselFullModal');

const CarouselFullModal = ({
    isOpen,
    className,
    modalOverlayClassName,
    headerClass,
    contentClass,
    contentLeftClass,
    contentCenterClass,
    contentRightClass,
    onAfterOpen,
    onRequestClose,
    withCloseIcon,
    shouldCloseOnEsc,
    shouldCloseOnOverlayClick,
    contentLeft,
    contentCenter,
    contentRight,
    imageSelected,
    updateImageSelected,
    arrows = false,
    dots = false, //navigation dots
    lazyLoad = 'ondemand', // 'progressive' is the other option
    infinite = true,
    speed = 500,
    slidesToShow = 1,
    slidesToScroll = 1,
    draggable = false, //leave it false cause it causes wierd behaviour
    centerMode = false, //center current slide
    accessibility = true,
    initialSlide = null,
    images = [],
}) => {
    const classes = useStyles();
    // Classes
    const rootClassNames = classnames(classes.root, className);
    const modalOverlayClassNames = classnames(classes.overlay, modalOverlayClassName);
    const headerClassNames = classnames(classes.header, headerClass);
    const contentClassNames = classnames(classes.content, contentClass);
    const contentLeftClassNames = classnames(classes.contentLeft, contentLeftClass);
    const contentCenterClassNames = classnames(classes.contentCenter, contentCenterClass);
    const contentRightClassNames = classnames(classes.contentRight, contentRightClass);

    const sliderRef = useRef(null);

    const beforeChange = useCallback(
        (current, next) => {
            updateImageSelected(next);
        },
        [updateImageSelected],
    );

    const onClickPrevious = useCallback(() => {
        sliderRef?.current?.slickPrev?.();
    }, []);

    const onClickNext = useCallback(() => {
        sliderRef?.current?.slickNext?.();
    }, []);

    const renderImages = useMemo(() => {
        if (!images?.length) return null;
        return images.map((image, index) => (
            <div key={index} className={classes.sliderImage}>
                <img src={image.strLink} alt={image.description} />
            </div>
        ));
    }, [classes.sliderImage, images]);

    if (!images?.length || imageSelected < 0 || !updateImageSelected) return null;

    const settings = {
        className: classes.slider,
        arrows,
        dots,
        lazyLoad,
        infinite,
        speed,
        slidesToShow,
        slidesToScroll,
        centerMode,
        draggable,
        initialSlide: initialSlide !== null ? initialSlide : imageSelected,
        accessibility,
        beforeChange,
        ref: (slider) => (sliderRef.current = slider),
    };

    return (
        <FullModalBlank
            className={rootClassNames}
            overlayClassName={modalOverlayClassNames}
            headerClass={headerClassNames}
            isOpen={isOpen}
            onAfterOpen={onAfterOpen}
            withCloseIcon={withCloseIcon}
            onRequestClose={onRequestClose}
            shoudCloseOnEsc={shouldCloseOnEsc}
            shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
            contentLeftClassName={contentLeftClassNames}
            contentCenterClassName={contentCenterClassNames}
            contentRightClassName={contentRightClassNames}
            contentLeft={contentLeft}
            contentCenter={contentCenter}
            contentRight={contentRight}
        >
            <div className={classes.contentBackground} />
            <div className={contentClassNames}>
                <Slick {...settings}>{renderImages}</Slick>
                <div className={classes.previousButton} onClick={onClickPrevious}>
                    <div className={classes.previousButtonContent}>
                        <Icon
                            name="chevron"
                            size="big"
                            color="white"
                            className={classes.chevronLeft}
                        />
                    </div>
                </div>
                <div className={classes.nextButton} onClick={onClickNext}>
                    <div className={classes.nextButtonContent}>
                        <Icon
                            name="chevron"
                            size="big"
                            color="white"
                            className={classes.chevronRight}
                        />
                    </div>
                </div>
            </div>
        </FullModalBlank>
    );
};

CarouselFullModal.propTypes = {
    //FullModal parameters
    isOpen: PropTypes.bool,
    modalClassName: PropTypes.string,
    modalOverlayClassName: PropTypes.string,
    headerClass: PropTypes.string,
    contentClass: PropTypes.string,
    contentLeftClass: PropTypes.string,
    contentCenterClass: PropTypes.string,
    contentRightClass: PropTypes.string,
    onAfterOpen: PropTypes.func,
    onRequestClose: PropTypes.func,
    withCloseIcon: PropTypes.bool,
    shouldCloseOnEsc: PropTypes.bool,
    shouldCloseOnOverlayClick: PropTypes.bool,
    contentLeft: PropTypes.func,
    contentCenter: PropTypes.func,
    contentRight: PropTypes.func,
    //Carousel parameters
    arrows: PropTypes.bool,
    dots: PropTypes.bool,
    lazyLoad: PropTypes.string,
    infinite: PropTypes.bool,
    speed: PropTypes.number,
    slidesToShow: PropTypes.number,
    slidesToScroll: PropTypes.number,
    draggable: PropTypes.bool,
    centerMode: PropTypes.bool,
    initialSlide: PropTypes.number,
    accessibility: PropTypes.bool,
    //Images
    images: PropTypes.array,
    imageSelected: PropTypes.number,
    updateImageSelected: PropTypes.func,
};

export default memo(CarouselFullModal);
